import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CameraIcon, XMarkIcon, ArrowPathIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { useIsMounted } from '../../hooks/useIsMounted';

type CaptureMode = 'face' | 'document';

interface WebcamCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (photoBase64: string) => void;
  /** Capture mode: 'face' for selfie (front camera), 'document' for document scan (rear camera on mobile) */
  mode?: CaptureMode;
  /** Optional fallback: called when camera access is denied, allows parent to trigger file upload */
  onFileUploadFallback?: () => void;
}

/** Detect mobile device via user agent */
const isMobileDevice = (): boolean =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

/** Check if the current context supports getUserMedia (requires HTTPS or localhost) */
const isSecureContext = (): boolean => {
  // window.isSecureContext is the standard check
  if (typeof window.isSecureContext === 'boolean') {
    return window.isSecureContext;
  }
  // Fallback: check protocol
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  return protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
};

/** Check if getUserMedia API is available */
const isCameraApiAvailable = (): boolean => {
  return !!(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function');
};

export const WebcamCapture: React.FC<WebcamCaptureProps> = ({
  isOpen,
  onClose,
  onCapture,
  mode = 'face',
  onFileUploadFallback,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [hasStream, setHasStream] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>(
    mode === 'document' && isMobileDevice() ? 'environment' : 'user'
  );
  const [isMobile] = useState(isMobileDevice);
  const isMounted = useIsMounted();
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const stopAllTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setHasStream(false);
  }, []);

  const startWebcam = useCallback(async () => {
    // Check secure context first
    if (!isSecureContext()) {
      setError(
        `Accès caméra impossible : la page doit être servie en HTTPS ou via localhost. ` +
        `Vous accédez actuellement via "${window.location.protocol}//${window.location.hostname}". ` +
        `Utilisez https:// ou accédez via localhost:${window.location.port || '3001'}.`
      );
      return;
    }

    if (!isCameraApiAvailable()) {
      setError(
        "L'API caméra n'est pas disponible dans ce navigateur. " +
        "Assurez-vous d'utiliser un navigateur récent (Chrome, Firefox, Edge, Safari) en HTTPS."
      );
      return;
    }

    try {
      setPermissionDenied(false);
      setError('');
      setIsLoading(true);

      // Stop any existing stream before starting a new one
      stopAllTracks();

      const constraints: MediaStreamConstraints = {
        video: {
          width: { ideal: mode === 'document' ? 1280 : (isMobile ? 720 : 1280) },
          height: { ideal: mode === 'document' ? 720 : (isMobile ? 1280 : 720) },
          facingMode: facingMode,
          ...(mode === 'face' && { aspectRatio: { ideal: 9 / 16 } }),
        },
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      if (!isMounted()) {
        // Component unmounted while waiting — clean up immediately
        mediaStream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = mediaStream;
      setHasStream(true);

      // Attach to video element and play
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        try {
          await videoRef.current.play();
        } catch (playErr) {
          console.warn('Video play() auto-start warning:', playErr);
        }
      }
    } catch (err: any) {
      if (!isMounted()) return;

      const isPermErr =
        err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError';

      setPermissionDenied(isPermErr);

      if (isPermErr) {
        setError(
          "Accès à la caméra refusé. Veuillez autoriser l'accès dans les paramètres de votre navigateur, ou utilisez l'upload de fichier."
        );
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('Aucune caméra détectée sur cet appareil.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('La caméra est utilisée par une autre application. Fermez les autres onglets/apps utilisant la caméra.');
      } else if (err.name === 'OverconstrainedError') {
        // Retry with simpler constraints
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true });
          if (!isMounted()) {
            fallbackStream.getTracks().forEach((t) => t.stop());
            return;
          }
          streamRef.current = fallbackStream;
          setHasStream(true);
          if (videoRef.current) {
            videoRef.current.srcObject = fallbackStream;
            await videoRef.current.play();
          }
          return;
        } catch {
          setError("Impossible d'accéder à la caméra avec les paramètres demandés.");
        }
      } else {
        setError(`Impossible d'accéder à la caméra: ${err.message || err.name || 'Erreur inconnue'}`);
      }
      console.error('Webcam error:', err);
    } finally {
      if (isMounted()) setIsLoading(false);
    }
  }, [facingMode, isMobile, isMounted, mode, stopAllTracks]);

  // Start webcam when modal opens
  useEffect(() => {
    if (isOpen) {
      startWebcam();
    }
    return () => {
      stopAllTracks();
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-attach stream to video element if the video ref changes (e.g. after re-render)
  useEffect(() => {
    if (hasStream && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch((err) => {
        console.warn('Video re-attach play() failed:', err);
      });
    }
  }, [hasStream, capturedPhoto]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  // Re-initialize camera when facingMode changes (camera toggle)
  useEffect(() => {
    if (isOpen && hasStream) {
      startWebcam();
    }
  }, [facingMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCamera = useCallback(() => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context || video.videoWidth === 0 || video.videoHeight === 0) {
      setError('La caméra n\'est pas encore prête. Attendez quelques secondes et réessayez.');
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // JPEG at 85% quality per requirements
    const photoBase64 = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedPhoto(photoBase64);
  }, []);

  const retakePhoto = useCallback(() => {
    setCapturedPhoto(null);
  }, []);

  const confirmPhoto = useCallback(() => {
    if (capturedPhoto && isMounted()) {
      onCapture(capturedPhoto);
      stopAllTracks();
      setCapturedPhoto(null);
      onClose();
    }
  }, [capturedPhoto, onCapture, onClose, stopAllTracks, isMounted]);

  const handleClose = useCallback(() => {
    stopAllTracks();
    if (isMounted()) {
      setCapturedPhoto(null);
      setPermissionDenied(false);
      setError('');
    }
    onClose();
  }, [onClose, stopAllTracks, isMounted]);

  if (!isOpen) return null;

  /** Render the appropriate overlay based on capture mode */
  const renderOverlay = () => {
    if (mode === 'document') {
      return (
        <>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div
              className="border-2 border-dashed border-yellow-400 rounded-lg"
              style={{ width: '80%', aspectRatio: '1.586', maxWidth: '500px' }}
            />
          </div>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative" style={{ width: '80%', aspectRatio: '1.586', maxWidth: '500px' }}>
              <div className="absolute top-0 left-0 w-6 h-6" style={{ borderTopWidth: '3px', borderLeftWidth: '3px', borderColor: '#facc15', borderStyle: 'solid' }} />
              <div className="absolute top-0 right-0 w-6 h-6" style={{ borderTopWidth: '3px', borderRightWidth: '3px', borderColor: '#facc15', borderStyle: 'solid' }} />
              <div className="absolute bottom-0 left-0 w-6 h-6" style={{ borderBottomWidth: '3px', borderLeftWidth: '3px', borderColor: '#facc15', borderStyle: 'solid' }} />
              <div className="absolute bottom-0 right-0 w-6 h-6" style={{ borderBottomWidth: '3px', borderRightWidth: '3px', borderColor: '#facc15', borderStyle: 'solid' }} />
            </div>
          </div>
          <div className="absolute bottom-2 sm:bottom-4 left-0 right-0 text-center px-2">
            <p className="text-white text-xs sm:text-sm bg-black bg-opacity-60 inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded">
              Placez le document dans le cadre
            </p>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-48 h-64 sm:w-64 sm:h-80 border-4 border-blue-500 rounded-lg opacity-50" />
        </div>
        <div className="absolute bottom-2 sm:bottom-4 left-0 right-0 text-center px-2">
          <p className="text-white text-xs sm:text-sm bg-black bg-opacity-50 inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded">
            Positionnez votre visage dans le cadre
          </p>
        </div>
      </>
    );
  };

  const modalTitle = mode === 'document' ? 'Scanner un Document' : 'Capture Photo';
  const videoAspectRatio = mode === 'document' ? '4/3' : (window.innerWidth < 640 ? '9/16' : '16/9');

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-2 sm:px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full w-full max-w-full">
          <div className="bg-white px-3 sm:px-4 pt-4 sm:pt-5 pb-3 sm:pb-4">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-medium text-gray-900">{modalTitle}</h3>
              <button
                type="button"
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Fermer"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Error message with file upload fallback */}
            {error && (
              <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-xs sm:text-sm text-red-800">{error}</p>
                {(permissionDenied || !isSecureContext()) && onFileUploadFallback && (
                  <button
                    type="button"
                    onClick={() => { onFileUploadFallback(); handleClose(); }}
                    className="mt-2 text-xs sm:text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Utiliser l&apos;upload de fichier à la place
                  </button>
                )}
                {!isSecureContext() && (
                  <p className="mt-2 text-xs text-gray-600">
                    Astuce : accédez à l&apos;application via{' '}
                    <code className="bg-gray-100 px-1 rounded">https://</code> ou{' '}
                    <code className="bg-gray-100 px-1 rounded">localhost:{window.location.port || '3001'}</code>{' '}
                    pour activer la caméra.
                  </p>
                )}
              </div>
            )}

            {/* Camera view or captured photo */}
            <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: videoAspectRatio }}>
              {!capturedPhoto ? (
                <>
                  {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div className="text-white text-sm">Activation de la caméra...</div>
                    </div>
                  )}
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    // @ts-ignore — webkit vendor attribute for iOS inline playback
                    webkit-playsinline="true"
                    className="w-full h-full object-cover"
                  />
                  {renderOverlay()}
                </>
              ) : (
                <img src={capturedPhoto} alt="Captured" className="w-full h-full object-cover" />
              )}
            </div>

            <canvas ref={canvasRef} className="hidden" />

            {capturedPhoto && (
              <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-xs sm:text-sm text-green-800">
                  {mode === 'document' ? 'Document capturé avec succès.' : 'Photo capturée avec succès.'}
                </p>
              </div>
            )}
          </div>

          {/* Footer with action buttons */}
          <div className="bg-gray-50 px-3 sm:px-4 py-3 sm:px-6 flex flex-col sm:flex-row sm:flex-row-reverse space-y-2 sm:space-y-0">
            {!capturedPhoto ? (
              <>
                <button
                  type="button"
                  onClick={capturePhoto}
                  disabled={!hasStream || isLoading}
                  className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2.5 sm:py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  <CameraIcon className="h-5 w-5 mr-2" />
                  {mode === 'document' ? 'Capturer le Document' : 'Capture Photo'}
                </button>
                {isMobile && hasStream && (
                  <button
                    type="button"
                    onClick={toggleCamera}
                    className="w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-2.5 sm:py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm min-h-[44px]"
                    aria-label="Basculer caméra"
                  >
                    <ArrowsRightLeftIcon className="h-5 w-5 mr-2" />
                    {facingMode === 'user' ? 'Caméra arrière' : 'Caméra avant'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-2.5 sm:py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm min-h-[44px]"
                >
                  Annuler
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={confirmPhoto}
                  className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2.5 sm:py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm min-h-[44px]"
                >
                  {mode === 'document' ? 'Utiliser cette image' : 'Utiliser cette photo'}
                </button>
                <button
                  type="button"
                  onClick={retakePhoto}
                  className="w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-2.5 sm:py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm min-h-[44px]"
                >
                  <ArrowPathIcon className="h-5 w-5 mr-2" />
                  Reprendre
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
